import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { NameDto } from '@/api/Common/Types'
import { DataCard, DataColumn, DataGrid } from '@/components/DataCard/DataCard'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import { Grid, GridCellProps, GridColumn } from '@progress/kendo-react-grid'
import { PermissionDto } from 'api/Identity/Types/PermissionDto'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import AddButton from 'components/AddButton/AddButton'
import { Modal } from 'components/CustomModal'
import {
  FormBaseErrorMessage,
  FormBaseFieldBlock,
  FormBaseFieldGroup,
  FormBaseFieldLabel,
  FormBaseRow,
} from 'components/Form/Form.styles'
import MoreButton from 'components/MoreButton/MoreButton'
import { MainTemplate } from 'components/Templates/MainTemplate'
import { Field, Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { links } from 'utils/featureToggles'
import { FormSubmitValues } from 'utils/formValidation'
import { hasPermission } from 'utils/permissionsValidator'

import { FeatureFlag, FeatureFlagVersion } from '../FeatureFlags'
import { FeatureFlagModal } from './FeatureFlagModal'

export type FeatureFlagFormSubmitValues = {
  id?: string
  name: string
  isEnabledDefault: boolean
  versions: FeatureFlagVersion[]
}

const FeatureFlagFormUI: React.FC<FormikProps<FeatureFlagFormSubmitValues> & OuterProps> = (
  props
) => {
  const navigate = useNavigate()
  const [isEdit, setEdit] = useState<boolean>(!props.featureFlag?.id)
  const [versionModal, setVersionModal] = useState<{
    data: FeatureFlagVersion | null
    visible: boolean
  }>({ data: null, visible: false })
  const { resetForm, handleSubmit, setFieldValue, permissions, errors, values, isValid } = props

  const cancel = () => {
    if (!props.featureFlag?.id) {
      return navigate(links.productFeatureFlags.url)
    }
    setEdit(false)
    resetForm()
  }

  const handleSubmitForm = () => {
    setEdit(false)
    handleSubmit()
  }

  const addVersion = (newVersion: FeatureFlagVersion) => {
    setFieldValue('versions', [...values.versions, newVersion])
    setVersionModal({ data: null, visible: false })
  }

  const editVersion = (newVersion: FeatureFlagVersion) => {
    const index = values.versions.findIndex(
      (x) => x.version?.name === versionModal.data?.version?.name
    )
    const newVersions = [...values.versions]
    newVersions[index] = newVersion

    setFieldValue('versions', newVersions)
    setVersionModal({ data: null, visible: false })
  }

  return (
    <React.Fragment>
      <MainTemplate>
        <Modal
          width={1300}
          visible={versionModal.visible}
          onClose={() => setVersionModal({ data: null, visible: false })}
        >
          <FeatureFlagModal
            data={versionModal.data}
            handleAdd={addVersion}
            handleEdit={editVersion}
          />
        </Modal>
        <Form>
          <DataGrid
            rightAddon={
              <ButtonGroup data-test='action-buttons'>
                {!isEdit ? (
                  <Button
                    type='button'
                    disabled={!hasPermission(permissions, PermissionsEnum.CanEditFeatureFlags)}
                    onClick={() => setEdit(true)}
                    data-test='edit-button'
                  >
                    Edit
                  </Button>
                ) : (
                  <React.Fragment>
                    <Button
                      variant='outline'
                      className='is-danger'
                      type='button'
                      onClick={cancel}
                      data-test='cancel-button'
                    >
                      Cancel
                    </Button>
                    <Button
                      key='button'
                      type='button'
                      onClick={handleSubmitForm}
                      disabled={!isValid}
                      data-test='save-button'
                    >
                      Save
                    </Button>
                  </React.Fragment>
                )}
              </ButtonGroup>
            }
          >
            <DataColumn>
              <DataCard title='Main' columns={2}>
                <FormBaseFieldBlock>
                  <FormBaseRow>
                    <FormBaseFieldLabel>Name</FormBaseFieldLabel>
                    <FormBaseFieldGroup>
                      <Field name='name' type='text' disabled={!isEdit} data-test='name-field' />
                    </FormBaseFieldGroup>
                  </FormBaseRow>
                  {errors.name && <FormBaseErrorMessage>{errors.name}</FormBaseErrorMessage>}
                </FormBaseFieldBlock>

                <FormBaseFieldBlock>
                  <FormBaseRow>
                    <FormBaseFieldLabel>Enabled</FormBaseFieldLabel>
                    <FormBaseFieldGroup>
                      <Field
                        name='isEnabledDefault'
                        type='checkbox'
                        disabled={!isEdit}
                        data-test='enabled-field'
                      />
                    </FormBaseFieldGroup>
                  </FormBaseRow>
                </FormBaseFieldBlock>
              </DataCard>

              <DataCard title='Versions'>
                <div className='flex flex-col gap-3'>
                  <AddButton
                    disabled={!isEdit}
                    text={'Add Version'}
                    onClick={() => setVersionModal({ visible: true, data: null })}
                  />
                  <Grid
                    data={values.versions}
                    resizable
                    expandField='expanded'
                    style={{ width: '100%' }}
                  >
                    <GridColumn width={250} field='version.name' title='Version' />
                    <GridColumn width={250} field='isEnabled' title='Enabled' />
                    <GridColumn
                      title=''
                      width={50}
                      cell={(data: GridCellProps) => (
                        <td>
                          <MoreButton
                            items={[
                              {
                                name: 'Edit',
                                disabled: !isEdit,
                                onClick: () =>
                                  setVersionModal({ visible: true, data: data.dataItem }),
                              },
                            ]}
                          />
                        </td>
                      )}
                    />
                  </Grid>
                </div>
              </DataCard>
            </DataColumn>
          </DataGrid>
        </Form>
      </MainTemplate>
    </React.Fragment>
  )
}

interface OuterProps {
  featureFlag?: FeatureFlag | null
  permissions: PermissionDto[]
  socialMediaTypes: NameDto[]

  onSubmit(values: FormSubmitValues<FeatureFlagFormSubmitValues>): Promise<void>
  forceRefresh: () => void
}

export const FeatureFlagForm = withFormik<OuterProps, FeatureFlagFormSubmitValues>({
  mapPropsToValues: ({ featureFlag }) => {
    return {
      ...featureFlag,
      id: featureFlag?.id || '',
      name: featureFlag?.name || '',
      isEnabledDefault: featureFlag?.isEnabledDefault || false,
      versions: featureFlag?.versions || [],
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      const { id, ...rest } = values
      await props.onSubmit(id ? { id, ...rest } : rest)
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<FeatureFlagFormSubmitValues> = {}

    if (!values.name) errors.name = 'Name is required'

    return errors
  },
  isInitialValid: ({ featureFlag }) => !!featureFlag,
})(FeatureFlagFormUI)
